import { MobXProviderContext, observer } from "mobx-react";
import React, { SyntheticEvent } from "react";
import { Modal } from "src/views/shared/components";
import { tx, txWithComponent } from "src/libs/i18n";
import { Button } from "src/views/shared/components";
import { Input } from "src/views/shared/components";
import { EmailValidator, TextValidator } from "src/libs/validators";
import * as s from "./FileTrackingForm.module.scss";
import { P } from "src/views/shared/components";
import { Checkbox } from "src/views/shared/components";
import { ReCaptcha } from "src/views/shared/components";
import { Icons } from "src/libs/assets";

export interface IDownloadTrackingButtonProps {
  children: JSX.Element;
  url: string;
  databaseId: number;
}

export interface IDownloadTrackingForm {
  name: string;
  email: string;
  tac: boolean;
  recaptcha: boolean;
}

export const InitialDownloadTrackingForm: IDownloadTrackingForm = {
  name: "",
  email: "",
  tac: false,
  recaptcha: false
};

export const FileTrackingForm = observer(({ type = "document", databaseId, fileTitle, postId }: { type?: "document" | "toolkit"; databaseId?: number, fileTitle?: string, postId?: number }) => {

  if (typeof databaseId === "undefined") return null;

  const [showForm, setShowForm] = React.useState(false);
  const [form, setForm] = React.useState<IDownloadTrackingForm>(InitialDownloadTrackingForm);
  const [requestStatus, setRequestStatus] = React.useState<"idle" | "pending" | "error">("idle");

  const { authenticationStore, userStore } = React.useContext(MobXProviderContext);

  const { currentUser } = userStore;

  const { authenticationStoreHydrated, userHasValidToken, trackDocument, trackToolkit } = authenticationStore;

  const { name, email, tac, recaptcha } = form;

  const isValid = useIsFormValid(form);

  const body = type === "document" ? { fileId: databaseId, fileTitle, postId, ...form } : { toolkitId: databaseId, ...form };

  const trackingFunction = type === "document" ? () => trackDocument(body) : () => trackToolkit(body);

  React.useEffect(() => {
    if (!authenticationStoreHydrated || hasViewed(databaseId)) return;

    if (userHasValidToken) {
      setForm({ ...form, name: `${currentUser?.firstName} ${currentUser?.lastName}`, email: currentUser?.email });
      setShowForm(true);
    } else {
      setShowForm(true);
    }
  }, [userHasValidToken, authenticationStoreHydrated]);

  const onSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();

    if (!isValid || requestStatus === "pending") return;

    setRequestStatus("pending");

    try {
      await trackingFunction();
      setHasViewed(databaseId);
      setRequestStatus("idle");
      setShowForm(false);
    } catch (e) {
      console.log(e);
      setRequestStatus("error");
    }
  };

  const dismissForm = () => {
    setShowForm(false);
    setHasViewed(databaseId);
  };

  return (
    <Modal open={showForm} onClose={dismissForm}>
      <form className={s.form} onSubmit={onSubmit}>
        <h3>{tx("document.trackingFormHeading")}</h3>
        <P className={s.heading} medium>
          {tx("document.trackingFormBody")}
        </P>
        <div className={s.inputContainer}>
          <img
            src={Icons.user_small}
            alt="close"
            className={s.inputIcon}
          />
          <Input name="name" type="text" value={name} onChange={value => setForm({ ...form, name: value })} placeholder={tx("input.firstAndLastName")} label={tx("input.firstAndLastName")} />
        </div>
        <div className={s.inputContainer}>
        <img
            src={Icons.email_small}
            alt="close"
            className={s.inputIcon}
          />
          <Input name="email" type="email" value={email} onChange={value => setForm({ ...form, email: value })} placeholder={tx("settings.inputs.emailPlaceholder")} label={tx("settings.inputs.emailLabel")} />
        </div>
        <div className={s.tacContainer}>
          <Checkbox type="check" onChange={() => setForm({ ...form, tac: !tac })} selected={tac} />
          <P className={s.tacText}>
            {txWithComponent("settings.inputs.termsApprovalText", [
              <a key="link1" className={s.tacLink}>
                {tx("settings.inputs.termsAndConditions")}
              </a>,
              <a key="link2" className={s.tacLink}>
                {tx("settings.inputs.privacyPolicy")}
              </a>
            ])}
          </P>
        </div>
        <div>
          <ReCaptcha reCaptchaValid={recaptcha} toggleReCaptcha={() => setForm({ ...form, recaptcha: !recaptcha })} />
        </div>
        <div className={s.buttonContainer}>
          <Button type="submit" size="medium" color="red" disabled={!isValid || requestStatus === "pending"}>
            {tx("misc.submit")}
          </Button>
          <Button type="button" size="medium" color="gray" onClick={dismissForm}>
            {tx("document.trackingFormSkip")}
          </Button>
        </div>
        <P small>{tx("document.trackingFormDisclaimer")}</P>
      </form>
    </Modal>
  );
});

function useIsFormValid(form: IDownloadTrackingForm) {
  const [isValid, setIsValid] = React.useState(false);

  React.useEffect(() => {
    const { name, email, tac, recaptcha } = form;
    setIsValid(EmailValidator(email, { errorName: "email" }).valid && TextValidator(name, { errorName: "empty_field" }).valid && tac && recaptcha);
  }, [form]);

  return isValid;
}

function setHasViewed(databaseId: number) {
  const currentItems: string | null = localStorage.getItem("documentUsage");

  if (!currentItems?.length) {
    localStorage.setItem("documentUsage", JSON.stringify([databaseId]));
  } else {
    localStorage.setItem("documentUsage", JSON.stringify([...JSON.parse(currentItems), databaseId]));
  }
}

function hasViewed(databaseId: number) {
  const currentItems: string | null = localStorage.getItem("documentUsage");

  if (currentItems == null) return false;

  return JSON.parse(currentItems).includes(databaseId);
}
